/**
 * @file Contexte d'injection de la source de données du flow de réservation.
 * @module packages.common.components.table-reservation.context.ReservationDataContext
 *
 * Le flow de réservation est agnostique de sa source de données : il ignore la
 * clé API (widget embarquable) comme le slug (site public). L'hôte injecte un
 * adapter exposant des opérations « nues » — sans companyId ni apiKey, capturés
 * en closure par l'adapter — que les étapes et hooks consomment via
 * `useReservationData()`.
 *
 * Contrat des opérations (toutes `async`, retournent des données déballées) :
 * - getAvailableDates({service, partySize, year?, month?}) → Array
 * - getTimeSlots({date, service, partySize, areaId?}) → {timeslots, serviceNote?, ...}
 * - getBookableRooms({date?, service?, partySize?}) → Array<{id, name, displayOrder, available?}>
 *   optionnelle : choix de salle en ligne ; le hook consommateur (useRoomSelection)
 *   se désactive si l'adapter ne l'expose pas
 * - getAvailableFormulas({service, date, partySize?, time?, dateTime?}, {signal?}) → {formulas, unavailableFormulas}
 *   `partySize` et `time` (HH:mm) pilotent les contraintes de formule : bornes
 *   de convives et salles imposées libres à l'heure choisie ; `dateTime` (ISO) est
 *   l'horodatage du créneau et prime sur `time` (créneaux après minuit).
 *   `unavailableFormulas` : formules aux salles pleines à cette heure, affichées grisées
 * - getFormulaDates({service, year, month}, {signal?}) → string[]
 * - evaluateCardHold(payload) → {success, data}
 * - preparePayment(payload) → {success, data}
 * - confirmPayment(payload) → Object
 * - createReservation(payload) → Object
 * - createWaitlistEntry(payload) → Object
 * - syncReservationDraft(payload) → Object — optionnelle : trace fire-and-forget
 *   d'un parcours non finalisé ; le hook consommateur (useReservationDraftSync)
 *   se désactive si l'adapter ne l'expose pas
 */
import React, {createContext, useContext} from 'react';
import PropTypes from 'prop-types';

const ReservationDataContext = createContext(null);

/**
 * Fournit l'adapter de données du flow de réservation à l'arbre des étapes.
 *
 * @param {Object} props
 * @param {Object} props.value - Adapter exposant les opérations de données
 * @param {React.ReactNode} props.children
 * @return {JSX.Element}
 */
export function ReservationDataProvider({value, children}) {
  return (
    <ReservationDataContext.Provider value={value}>
      {children}
    </ReservationDataContext.Provider>
  );
}

ReservationDataProvider.propTypes = {
  value: PropTypes.object.isRequired,
  children: PropTypes.node,
};

/**
 * Accède à l'adapter de données injecté par l'hôte.
 *
 * @return {Object} Adapter de données de réservation
 * @throws {Error} Si appelé hors d'un `<ReservationDataProvider>`
 */
export function useReservationData() {
  const context = useContext(ReservationDataContext);
  if (!context) {
    throw new Error('useReservationData doit être utilisé dans un <ReservationDataProvider>');
  }
  return context;
}
